// Home/dashboard area: ProjectsPage, action tiles, the projects panel,
// project detail, the transcription list, and the shared upload flow.
//
// A few keys are deliberately shared across source files rather than
// duplicated per-file:
//  - `actionTiles` is the single source for the "Create transcription /
//    Record & transcribe / Caption your video" trio's copy — both the home
//    tiles (action-tiles.js) and ProjectDetailPage's "New Transcription"
//    dropdown reuse it.
//  - `uploadFlow` holds copy shared by ProjectDetailPage.js and
//    TranscriptionPage.js: the caption UploadDialog's title/prompt, the
//    upload-result toasts, and the "missing transcription id" error thrown
//    from applyCompletedUpload in both files.
//  - `transcriptionList` holds copy shared by ProjectDetailPage.js and
//    TranscriptionPage.js for acting on a single transcription (its
//    rename/delete dialogs, the "N files" count, and the move-to-project
//    toasts also duplicated in ProjectsLayout.js) — both pages render the
//    same TranscriptionList and offer the same actions on its rows, so this
//    keeps that copy defined once instead of twice.
export const home = {
  en: {
    home: {
      emptyTitle: "No projects yet",
      emptyDescription: "Create a project to start grouping your transcriptions.",
      newProject: "New project",
      selectTitle: "Select a project",
      selectDescription: "Pick one from the sidebar, or create a new one.",
    },
    actionTiles: {
      createTitle: "Create Transcriptions",
      createDescription: "Upload audio/video files in any format and get accurate transcripts.",
      recordTitle: "Record & Transcribe",
      recordDescription: "Record audio directly and get its transcription.",
      captionTitle: "Caption Your Video",
      captionDescription: "Upload a video to generate captions and add them with custom styling options.",
    },
    projectsPanel: {
      title: "Projects",
      newProject: "New project",
      empty: "No projects yet.",
    },
    projectDetail: {
      backToProjects: "Back to projects",
      projectActions: "Project actions",
      menuRename: "Rename",
      menuDelete: "Delete",
      renameProjectTitle: "Rename project",
      deleteProjectTitle: "Delete project",
      deleteProjectDescription:
        "This will permanently delete this project. Transcriptions inside it will not be deleted — they'll just no longer be grouped under it.",
      nameLabel: "Name",
      saveLabel: "Save",
      cancelLabel: "Cancel",
      importLabel: "Import",
      uploadingLabel: "Uploading…",
      newTranscription: "New Transcription",
    },
    transcriptionList: {
      colName: "Name",
      colLanguage: "Language",
      colDuration: "Duration",
      colStatus: "Status",
      colProject: "Project",
      colCreated: "Created",
      sourceRecorded: "Recorded",
      sourceUploaded: "Uploaded",
      sourceCaption: "Captioned",
      statusFailed: "Failed",
      statusProcessing: "Processing",
      statusDone: "Done",
      phasePreparing: "Preparing",
      phaseUploadingPercent: (pct) => `Uploading ${pct}%`,
      phaseValidating: "Validating",
      phaseCancelling: "Cancelling",
      phaseUploading: "Uploading",
      cancelUpload: "Cancel upload",
      rename: "Rename",
      moveToProject: "Move to project",
      noProject: "No project",
      delete: "Delete",
      deleteTranscriptionTitle: "Delete transcription",
      deleteTranscriptionDescription: "This will permanently delete this transcription. This action cannot be undone.",
      renameTranscriptionTitle: "Rename transcription",
      nameLabel: "Name",
      saveLabel: "Save",
      cancelLabel: "Cancel",
      filesCount: (n) => (n === 1 ? "1 file" : `${n} files`),
      movedToProject: (projectName) => `Moved to ${projectName}`,
      removedFromProject: "Removed from project",
    },
    projectsLayout: {
      createProjectTitle: "New project",
      nameLabel: "Name",
      createLabel: "Create",
    },
    uploadFlow: {
      uploadComplete: "Upload complete. Transcription is processing.",
      uploadCancelled: "Upload cancelled.",
      missingIdError: "The uploaded file did not include its transcription ID.",
      captionUploadTitle: "Upload your video",
      captionUploadPrompt: "Drag and drop a video file",
    },
  },
  ar: {
    home: {
      emptyTitle: "لا توجد مشاريع بعد",
      emptyDescription: "أنشئ مشروعًا لتبدأ بتجميع تفريغاتك الصوتية.",
      newProject: "مشروع جديد",
      selectTitle: "اختر مشروعًا",
      selectDescription: "اختر واحدًا من الشريط الجانبي، أو أنشئ مشروعًا جديدًا.",
    },
    actionTiles: {
      createTitle: "حول الصوت إلى كتابة",
      createDescription: "ارفع ملفات صوتية أو مرئية بأي صيغة واحصل على نصوص دقيقة.",
      recordTitle: "حول تسجيلك إلى كتابة",
      recordDescription: "سجّل الصوت مباشرة واحصل على نص مكتوب بكلامك.",
      captionTitle: "ولّد كابشن للڤيديو",
      captionDescription: "ارفع فيديو لإنشاء كابشن له وإضافتها بخيارات تنسيق مخصصة.",
    },
    projectsPanel: {
      title: "المشاريع",
      newProject: "مشروع جديد",
      empty: "لا توجد مشاريع بعد.",
    },
    projectDetail: {
      backToProjects: "العودة إلى المشاريع",
      projectActions: "خيارات المشروع",
      menuRename: "إعادة تسمية",
      menuDelete: "حذف",
      renameProjectTitle: "إعادة تسمية المشروع",
      deleteProjectTitle: "حذف المشروع",
      deleteProjectDescription:
        "سيؤدي هذا إلى حذف هذا المشروع نهائيًا. لن تُحذف التفريغات الصوتية الموجودة بداخله — فقط لن تعود مجمّعة ضمنه.",
      nameLabel: "الاسم",
      saveLabel: "حفظ",
      cancelLabel: "إلغاء",
      importLabel: "استيراد",
      uploadingLabel: "جارٍ الرفع…",
      newTranscription: "تفريغ جديد",
    },
    transcriptionList: {
      colName: "الاسم",
      colLanguage: "اللغة",
      colDuration: "المدة",
      colStatus: "الحالة",
      colProject: "المشروع",
      colCreated: "تاريخ الإنشاء",
      sourceRecorded: "تسجيل",
      sourceUploaded: "رفع",
      sourceCaption: "ترجمة فيديو",
      statusFailed: "فشل",
      statusProcessing: "جارٍ المعالجة",
      statusDone: "مكتمل",
      phasePreparing: "جارٍ التحضير",
      phaseUploadingPercent: (pct) => `جارٍ الرفع ${pct}%`,
      phaseValidating: "جارٍ التحقق",
      phaseCancelling: "جارٍ الإلغاء",
      phaseUploading: "جارٍ الرفع",
      cancelUpload: "إلغاء الرفع",
      rename: "إعادة تسمية",
      moveToProject: "نقل إلى مشروع",
      noProject: "بلا مشروع",
      delete: "حذف",
      deleteTranscriptionTitle: "حذف التفريغ الصوتي",
      deleteTranscriptionDescription: "سيؤدي هذا إلى حذف هذا التفريغ الصوتي نهائيًا. لا يمكن التراجع عن هذا الإجراء.",
      renameTranscriptionTitle: "إعادة تسمية التفريغ الصوتي",
      nameLabel: "الاسم",
      saveLabel: "حفظ",
      cancelLabel: "إلغاء",
      filesCount: (n) => `${n} ملف`,
      movedToProject: (projectName) => `تم النقل إلى ${projectName}`,
      removedFromProject: "تمت إزالته من المشروع",
    },
    projectsLayout: {
      createProjectTitle: "مشروع جديد",
      nameLabel: "الاسم",
      createLabel: "إنشاء",
    },
    uploadFlow: {
      uploadComplete: "اكتمل الرفع. التفريغ الصوتي قيد المعالجة.",
      uploadCancelled: "تم إلغاء الرفع.",
      missingIdError: "الملف الذي تم رفعه لم يتضمن معرّف التفريغ الصوتي.",
      captionUploadTitle: "ارفع فيديوك",
      captionUploadPrompt: "اسحب ملف فيديو وأفلته هنا",
    },
  },
};
